import type { LintReport } from '@jsonfmt/core';

import { groupRepairs, listRepairs } from '@/features/repairSummary';
import type { Command, CommandResult, DocumentSnapshot, Notice } from '@/features/types';

import {
  isSafeRuleId,
  orderDiagnostics,
  problemCursor,
  type ProblemCursor,
  safeFixableCount,
  stepIndex,
} from './problemList';

/**
 * The four lint commands — docs/ARCHITECTURE.md §4.9, ADR-0012, ADR-0014.
 *
 * A command returns its effects AS DATA and performs nothing itself; `src/app/runCommand.ts`
 * is the single place that turns a `CommandResult` into a document change, a toast and a
 * reveal. `notice` is one optional field, so "at most one toast per action" is a property of
 * the type rather than a convention anyone has to remember.
 *
 * ── THE SAFETY CONTRACT, which is the reason this file exists ───────────────────────────
 *
 * `fixAll` must never apply a `heuristic` fix. It is guarded three times over, at three
 * different layers, because this is the exact failure that made the legacy fixer unsafe —
 * it applied everything it could and turned `{"a":NaN}` into `{"a":"NaN"}`, silently
 * changing a number into a string in a tool whose entire job is not to change your data:
 *
 *   1. `isEnabled` counts SAFE fixes only, so the control is not even offered on a document
 *      where "Fix all" could do nothing (`counts.fixable` would have offered it).
 *   2. `run` passes `allowHeuristic: false` explicitly. Never a variable, never a parameter —
 *      there is no code path in this repository that passes `true` to a bulk repair.
 *   3. The result is AUDITED: every applied rule id is checked against the `safe` half of
 *      `ALL_REPAIR_RULES`, and a single heuristic rule in `applied` discards the entire
 *      result and keeps the user's bytes. Guard 3 can only fire if the engine broke its own
 *      contract, and `commands.test.ts` breaks it on purpose to prove the guard fires.
 *
 * Heuristic fixes are not banned — they are `applyFix`'s job: one at a time, on a click, on
 * the row that explains what is being guessed at. That is the trade ADR-0012 makes.
 */

/**
 * "Problems" for the purposes of a toast: errors and warnings. Notes (`precision-loss`,
 * `empty-document`) are facts about a valid document, and counting them would report
 * "1 problem remains" over JSON that parses.
 */
function problemCount(report: LintReport): number {
  return report.counts.errors + report.counts.warnings;
}

/** One toast id per command, so hammering the control replaces rather than stacks. */
const FIX_ALL_TOAST = 'lint.fix-all';
const APPLY_FIX_TOAST = 'lint.apply-fix';

// ── fixAll ──────────────────────────────────────────────────────────────────────────────

export const fixAll: Command = {
  id: 'lint.fix-all',
  labelKey: 'action.fixAll',
  icon: 'spark',
  group: 'transform',
  variant: 'tinted',
  // Lemon: "repaired by the tool" — the same hue as the repair banner and the Repair log, so
  // the button and the report of what it did are visibly one thing (ADR-0101 §1).
  hue: 'lemon',

  isEnabled(doc: DocumentSnapshot): boolean {
    // A stale report describes a different text (ADR-0009). Repairing against it would be
    // acting on a verdict we have already been told not to trust.
    if (doc.report === null || doc.reportVersion < doc.version) return false;
    return safeFixableCount(doc.report) > 0;
  },

  /**
   * The lemon count pill (ADR-0101 §7.1). The SAME number `isEnabled` thresholds, and the
   * same staleness guard: a pill counting a superseded report would promise repairs against
   * text the user has already changed, on a button that is disabled for saying so.
   */
  badge(doc: DocumentSnapshot): number {
    if (doc.report === null || doc.reportVersion < doc.version) return 0;
    return safeFixableCount(doc.report);
  },

  async run(ctx, doc): Promise<CommandResult> {
    if (doc.text.length === 0) {
      return { notice: { tone: 'info', key: 'toast.error.empty', id: FIX_ALL_TOAST } };
    }

    // Guard 2. The engine re-analyses from the text rather than trusting `doc.report`, so
    // this is authoritative even if the snapshot's report is a version behind; and it brings
    // the driver's whole safety net with it — conflict resolution, the oscillation guard and
    // the `reverted-unsafe` rollback (ADR-0012).
    const result = await ctx.services.analyzer.repair(doc.text, { allowHeuristic: false });

    // Guard 3.
    const violation = result.applied.find((applied) => !isSafeRuleId(applied.ruleId));
    if (violation !== undefined) {
      return { notice: { tone: 'error', key: 'toast.error.unexpected', id: FIX_ALL_TOAST } };
    }

    const remaining = problemCount(result.after);

    if (!result.changed) {
      // `already-clean`, `no-progress` or `reverted-unsafe`. The bytes are untouched in all
      // three, and saying so beats a success toast over a document that did not change.
      return {
        notice: {
          tone: 'info',
          key: 'toast.repair.remaining',
          params: { count: remaining },
          id: FIX_ALL_TOAST,
        },
      };
    }

    // One toast per action, and a warning outranks a success (recon §1.2): a partially
    // repaired document is a document that still needs the user, and telling them how many
    // fixes landed matters less than telling them the job is not finished. The full
    // breakdown — every rule, with its multiplier — is the repair banner's job (DESIGN §6.5),
    // and `repair` below is how the banner gets it. That field is why the persistent bar can
    // now say what it repaired even on this path: it used to carry only `toast.repair.remaining`
    // and so announced AUTO-REPAIRED over a document whose bytes it had rewritten without
    // ever saying that, or what, it had changed.
    const report = {
      chips: groupRepairs(result.applied, ctx.catalog),
      // One row per edit, with the bytes on both sides, for the Repair log (ADR-0101 §7.15).
      entries: listRepairs(result.applied, ctx.catalog),
      residual: remaining,
      formatted: false,
      source: 'fix-all' as const,
    };

    const notice: Notice =
      remaining > 0
        ? {
            tone: 'warning',
            key: 'toast.repair.remaining',
            params: { count: remaining },
            id: FIX_ALL_TOAST,
            repair: report,
          }
        : {
            tone: 'success',
            key: 'toast.repair.applied',
            params: { count: result.applied.length },
            id: FIX_ALL_TOAST,
            repair: report,
          };

    return { document: { text: result.text, label: 'lint.fix-all' }, notice };
  },
};

// ── the three cursor-driven commands ────────────────────────────────────────────────────

export interface ProblemCommands {
  /** Applies the fix of the currently selected row. */
  readonly applyFix: Command;
  /** The same command bound to one row — what a row's own Fix button runs. */
  applyFixAt(index: number): Command;
  readonly nextProblem: Command;
  readonly previousProblem: Command;
}

function diagnosticsOf(doc: DocumentSnapshot) {
  return orderDiagnostics(doc.report?.diagnostics ?? []);
}

/**
 * The cursor is INJECTED rather than imported, so `commands.test.ts` runs against its own
 * instance and no test can leave state behind for the next one.
 */
export function createProblemCommands(cursor: ProblemCursor): ProblemCommands {
  const applyFixAt = (target: number | 'selected'): Command => ({
    id: 'lint.apply-fix',
    labelKey: 'action.fix',
    icon: 'spark',
    group: 'transform',

    isEnabled(doc: DocumentSnapshot): boolean {
      if (doc.report === null || doc.reportVersion < doc.version) return false;
      const list = diagnosticsOf(doc);
      const index = target === 'selected' ? cursor.getSnapshot() : target;
      return list[index]?.fix !== undefined;
    },

    run(_ctx, doc): CommandResult {
      const list = diagnosticsOf(doc);
      const index = target === 'selected' ? cursor.getSnapshot() : target;
      const d = list[index];
      const fix = d?.fix;
      // Nothing selected, or the report moved underneath the click. No effects at all: an
      // error toast for "you pressed a button that was not there" is noise, not information.
      if (d === undefined || fix === undefined) return {};

      cursor.select(index);

      return {
        edits: fix.edits,
        // A COLLAPSED span, not `d.span`. These offsets describe the text BEFORE the edits,
        // and `runCommand` applies the edits before it reveals; a caret at the start of the
        // problem is off by at most the length of one fix, while a range would visibly
        // straddle the wrong text.
        reveal: { from: d.span.from, to: d.span.from },
        notice: {
          // Applying a heuristic fix is a guess the user consented to, and the toast says so
          // in tone. `safe` is a plain success.
          tone: fix.confidence === 'safe' ? 'success' : 'warning',
          key: 'toast.repair.applied',
          params: { count: 1 },
          id: APPLY_FIX_TOAST,
        },
      };
    },
  });

  const step = (delta: 1 | -1, id: string, labelKey: string): Command => ({
    id,
    labelKey,
    icon: delta > 0 ? 'arrow-down' : 'arrow-up',
    group: 'view',
    // F8 / Shift-F8 is `lintKeymap`'s own vocabulary, which we keep even though we do not
    // use the `linter()` facet (ADR-0009), so muscle memory from any CodeMirror editor works.
    shortcut: delta > 0 ? 'F8' : 'Shift-F8',

    isEnabled(doc: DocumentSnapshot): boolean {
      return (doc.report?.diagnostics.length ?? 0) > 0;
    },

    run(ctx, doc): CommandResult {
      const list = diagnosticsOf(doc);
      const index = stepIndex(cursor.getSnapshot(), delta, list.length);
      const d = index < 0 ? undefined : list[index];
      if (d === undefined) return {};

      /*
       * OPEN THE LIST BEFORE POINTING AT A ROW IN IT — ADR-0101 §8.2.
       *
       * Until ADR-0101 the problems panel was always on screen, so "next problem" only had to
       * move a cursor. It is now behind the status door and collapsed on every load, and a F8
       * that silently advanced `aria-current` inside a hidden list would move a selection
       * nobody can see — while the editor DID scroll, so the user would be told they had
       * arrived somewhere without being shown what.
       *
       * It is called AFTER the `undefined` guard on purpose: F8 over a clean document opens
       * nothing. An empty drawer is the "Problems 0" affordance §0 item 6 removed.
       *
       * Focus is deliberately NOT moved into the drawer here, unlike the door's own click
       * (§8.2's table). The user pressed a key to go to a problem in the TEXT; taking the
       * keyboard out of the editor would undo the jump they just asked for.
       */
      ctx.ui.showProblems();

      // The acknowledged dent in "commands perform nothing": moving the cursor is what makes
      // the panel's `aria-current` and F8 the same selection. The effect that crosses the
      // application boundary — the reveal — is still returned as data.
      cursor.select(index);
      return { reveal: d.span };
    },
  });

  return {
    applyFix: applyFixAt('selected'),
    applyFixAt: (index: number) => applyFixAt(index),
    nextProblem: step(1, 'lint.next-problem', 'action.nextProblem'),
    previousProblem: step(-1, 'lint.previous-problem', 'action.previousProblem'),
  };
}

const bound = createProblemCommands(problemCursor);

export const applyFix: Command = bound.applyFix;
export const applyFixAt = bound.applyFixAt;
export const nextProblem: Command = bound.nextProblem;
export const previousProblem: Command = bound.previousProblem;

/** What `features/registry.ts` spreads into its flat command list. */
export const lintCommands: readonly Command[] = [fixAll, applyFix, nextProblem, previousProblem];
